import { useCallback, useEffect, useRef, useState } from "react";

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export function useApi<T>(
  fetcher: () => Promise<T>,
  deps: unknown[] = []
): UseApiState<T> & { refetch: () => void } {
  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    loading: true,
    error: null,
  });

  // Every call site passes an inline `() => api.xxx(...)` fetcher, so its identity
  // changes on every render — it is not a stable dependency. `fetcherRef` decouples
  // `fetch`'s identity (and thus when it re-runs) from that per-render churn: `fetch`
  // still only changes when `deps` changes (the caller-controlled refetch trigger,
  // unchanged behavior), while always invoking the *latest* fetcher instead of a
  // closure captured when `deps` last changed. This is the honest dependency: `deps`
  // is a caller-provided array this generic hook forwards, which ESLint's
  // exhaustive-deps rule cannot statically verify (non-literal array) — that is
  // inherent to the hook's generic design, not a suppressed real bug, so no
  // eslint-disable is used or needed here.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await fetcherRef.current();
      setState({ data, loading: false, error: null });
    } catch (err) {
      setState({
        data: null,
        loading: false,
        error: err instanceof Error ? err.message : "Request failed",
      });
    }
  }, deps); // deps passed explicitly by caller

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { ...state, refetch: fetch };
}
